import { formatInTimeZone } from 'date-fns-tz';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTimezoneAbbreviation } from 'shared-utils';

import { useBannerClass } from 'src/hooks/useBannerClass';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { globalVariables } from 'lib/globalVariables';
import { teamUtils } from 'lib/teams';

const DAY_MS = 24 * 60 * 60 * 1000;

function countdownValues({ cutoffAt, timeZone, now }: { cutoffAt: string; timeZone: string; now: Date }) {
  // Rounded up and floored at 1: a tab left open past the cut-off keeps `warned` until the API refuses it.
  const count = Math.max(1, Math.ceil((new Date(cutoffAt).getTime() - now.getTime()) / DAY_MS));
  const date = formatInTimeZone(new Date(cutoffAt), timeZone, 'd MMM yyyy');
  const zone = formatTimezoneAbbreviation(timeZone, new Date(cutoffAt).getTime());
  return { count, date, zone };
}

export const TeamEnforcementBanner = () => {
  const { t } = useTranslation();
  const { details } = useConnectedClient();
  const usePlatform = details?.ConnectedTeam?.capabilities?.usePlatform;
  const cutoffAt = usePlatform?.status === 'warned' ? usePlatform.cutoffAt : null;
  const timeZone = usePlatform?.cutoffTimeZone;
  const showBanner = !!cutoffAt && !!timeZone && !globalVariables.getEnvironmentVariables().isDemo;

  useBannerClass(showBanner);

  if (!showBanner) {
    return null;
  }

  const countdown = t(
    'sections.teamEnforcement.warning.countdown',
    countdownValues({ cutoffAt, timeZone, now: new Date() }),
  );
  const message = teamUtils.isAdminByRoleId(details?.teamRole)
    ? t('sections.teamEnforcement.warning.admin', { countdown })
    : t('sections.teamEnforcement.warning.member', { countdown });

  return (
    <div
      role="status"
      className="flex shrink-0 items-start gap-2 bg-warning-foreground px-4 py-3 text-xs text-foreground sm:text-sm"
    >
      <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
      <p>
        <strong>{t('sections.teamEnforcement.warning.title')}</strong> {message}
      </p>
    </div>
  );
};
